import React, { useEffect, useState, useRef } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Logo } from '@/components/ui/Logo';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { clearSettingsCache } from '@/utils/printDocs';
import { downloadJSON, readJSONFile } from '@/utils/exportUtils';
import { Save, Download, Upload, Database, AlertTriangle, FileText, CreditCard } from 'lucide-react';

export const Settings = () => {
  const [settings, setSettings] = useState({
    company_name: 'PC NERDS',
    tagline: 'BUY • BUILD • UPGRADE',
    phone: '',
    email: '',
    address: '',
    website: '',
    tax_id: '',
    currency: 'LKR',
    invoice_title: 'Invoice',
    invoice_footer: '',
    invoice_terms: '',
    invoice_accent_color: '#6d28d9',
    invoice_show_logo: true,
    invoice_show_tagline: true,
    invoice_show_signatures: true,
    invoice_thank_you_message: '',
    invoice_serial_display: 'Grouped',
    payment_methods: 'Cash,Card,Bank Transfer,Cheque,Online,Koko,Mintpay,PayHere'
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const fileInputRef = useRef(null);
  const { isAdmin, isSuperAdmin } = useAuth();

  useEffect(() => {
    loadSettings();
  }, []);

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await api.backup();
      downloadJSON(res.data, 'pcnerds_backup');
    } catch (error) {
      alert('Backup failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreClick = () => {
    fileInputRef.current?.click();
  };

  const handleRestoreFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const clearExisting = window.confirm(
      'CLEAR ALL EXISTING DATA before restoring?\n\n' +
      'Click OK to wipe and replace.\n' +
      'Click Cancel to merge (keep existing data, add backup data).'
    );

    if (!window.confirm(`Confirm restore from ${file.name}?\nThis action cannot be undone.`)) {
      e.target.value = '';
      return;
    }

    setRestoreLoading(true);
    try {
      const data = await readJSONFile(file);
      const res = await api.restore(data, clearExisting);
      alert(`Restore complete!\n\nRecords restored: ${JSON.stringify(res.data.counts, null, 2)}`);
      window.location.reload();
    } catch (error) {
      alert('Restore failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRestoreLoading(false);
      e.target.value = '';
    }
  };

  const loadSettings = async () => {
    try {
      const res = await api.getSettings();
      setSettings(res.data);
    } catch (e) {
      console.error('Error loading settings:', e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaved(false);
    try {
      await api.updateSettings(settings);
      clearSettingsCache(); // refresh print cache
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      alert('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card title="Company Information">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              This information appears on all printed documents (invoices, RMAs, repairs, GRNs).
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Company Name"
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  data-testid="settings-company-name"
                />
                <Input
                  label="Tagline"
                  value={settings.tagline}
                  onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                  data-testid="settings-tagline"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Phone"
                  placeholder="+1 (555) 123-4567"
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  data-testid="settings-phone"
                />
                <Input
                  label="Email"
                  type="email"
                  placeholder="info@pcnerds.com"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  data-testid="settings-email"
                />
              </div>

              <Textarea
                label="Address"
                placeholder="Street, City, State, ZIP"
                rows={2}
                value={settings.address}
                onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                data-testid="settings-address"
              />

              <div className="grid grid-cols-3 gap-4">
                <Input
                  label="Website"
                  placeholder="www.pcnerds.com"
                  value={settings.website}
                  onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                  data-testid="settings-website"
                />
                <Input
                  label="Tax ID / VAT"
                  value={settings.tax_id}
                  onChange={(e) => setSettings({ ...settings, tax_id: e.target.value })}
                  data-testid="settings-tax-id"
                />
                <Input
                  label="Currency Symbol"
                  value={settings.currency}
                  onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                  data-testid="settings-currency"
                />
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
                {saved && (
                  <span className="text-green-600 dark:text-green-400 text-sm font-medium" data-testid="settings-saved-message">
                    ✓ Settings saved successfully!
                  </span>
                )}
                <div className="ml-auto">
                  <Button type="submit" variant="primary" disabled={loading || !isAdmin()} data-testid="save-settings">
                    <Save size={16} className="mr-2" />
                    {loading ? 'Saving...' : 'Save Settings'}
                  </Button>
                </div>
              </div>

              {!isAdmin() && (
                <p className="text-xs text-orange-600 dark:text-orange-400 text-right">
                  Only admins can modify settings
                </p>
              )}
            </form>
          </Card>
        </div>

        {/* Preview Card */}
        <div>
          <Card title="Preview">
            <div className="flex flex-col items-center text-center gap-3 p-4">
              <Logo size="xl" />
              <h2 className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {settings.company_name}
              </h2>
              <p className="text-xs tracking-[2px] text-cyan-500">{settings.tagline}</p>
              <div className="w-full border-t border-gray-200 dark:border-gray-700 my-2"></div>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                {settings.phone && <p>📞 {settings.phone}</p>}
                {settings.email && <p>✉️ {settings.email}</p>}
                {settings.address && <p>📍 {settings.address}</p>}
                {settings.website && <p>🌐 {settings.website}</p>}
                {settings.tax_id && <p className="font-mono text-xs">Tax ID: {settings.tax_id}</p>}
              </div>
              <p className="text-xs text-gray-400 mt-3">This is how it will appear on documents</p>
            </div>
          </Card>
        </div>
      </div>

      {/* Invoice Template Customization */}
      <Card title={<div className="flex items-center gap-2"><FileText size={20} /> Invoice Template</div>}>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Customize how your invoices look when printed. Changes apply immediately to all new prints.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Document Title"
              value={settings.invoice_title}
              onChange={(e) => setSettings({ ...settings, invoice_title: e.target.value })}
              placeholder="Invoice, Sales Receipt, Tax Invoice..."
              data-testid="settings-invoice-title"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Accent Color</label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={settings.invoice_accent_color}
                  onChange={(e) => setSettings({ ...settings, invoice_accent_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                  data-testid="settings-accent-color"
                />
                <Input
                  value={settings.invoice_accent_color}
                  onChange={(e) => setSettings({ ...settings, invoice_accent_color: e.target.value })}
                  placeholder="#6d28d9"
                  className="flex-1 font-mono"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-6">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={settings.invoice_show_logo}
                  onChange={(e) => setSettings({ ...settings, invoice_show_logo: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="settings-show-logo"
                />
                <span>Show Logo</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={settings.invoice_show_tagline}
                  onChange={(e) => setSettings({ ...settings, invoice_show_tagline: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="settings-show-tagline"
                />
                <span>Show Tagline</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={settings.invoice_show_signatures}
                  onChange={(e) => setSettings({ ...settings, invoice_show_signatures: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="settings-show-signatures"
                />
                <span>Show Signatures</span>
              </label>
            </div>
          </div>

          <Input
            label="Thank You Message"
            placeholder="Thank you for your business — visit us again!"
            value={settings.invoice_thank_you_message}
            onChange={(e) => setSettings({ ...settings, invoice_thank_you_message: e.target.value })}
            data-testid="settings-thank-you"
          />

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Serial Number Display Mode</label>
            <select
              value={settings.invoice_serial_display || 'Grouped'}
              onChange={(e) => setSettings({ ...settings, invoice_serial_display: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-[var(--surface)] border border-[var(--border)] text-sm"
              data-testid="settings-serial-display"
            >
              <option value="Grouped">Grouped (Recommended) — single row per product with serials listed below</option>
              <option value="Individual">Individual Line Items — one row per serial</option>
            </select>
            <p className="text-xs text-[var(--text-muted)] mt-1">Controls how multi-quantity serialized items are printed on invoices.</p>
          </div>

          <Textarea
            label="Terms & Conditions"
            placeholder="Goods once sold cannot be returned without RMA approval. Warranty subject to manufacturer terms..."
            rows={3}
            value={settings.invoice_terms}
            onChange={(e) => setSettings({ ...settings, invoice_terms: e.target.value })}
            data-testid="settings-invoice-terms"
          />

          <Textarea
            label="Custom Footer Text"
            placeholder="Bank details, refund policy, or any other message..."
            rows={2}
            value={settings.invoice_footer}
            onChange={(e) => setSettings({ ...settings, invoice_footer: e.target.value })}
            data-testid="settings-invoice-footer"
          />
        </form>
      </Card>

      {/* Payment Methods Configuration */}
      <Card title={<div className="flex items-center gap-2"><CreditCard size={20} /> Payment Methods</div>}>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          Configure which payment methods appear in the invoice form (comma-separated). Common methods: Cash, Card, Bank Transfer, Koko, Mintpay, PayHere, FriMi, Genie, Dialog Genie, etc.
        </p>
        <Textarea
          value={settings.payment_methods}
          onChange={(e) => setSettings({ ...settings, payment_methods: e.target.value })}
          rows={2}
          placeholder="Cash,Card,Bank Transfer,Koko,Mintpay,PayHere"
          data-testid="settings-payment-methods"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {settings.payment_methods.split(',').map(m => m.trim()).filter(m => m).map((method, idx) => (
            <span
              key={idx}
              className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium"
              data-testid={`payment-method-chip-${method.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {method}
            </span>
          ))}
        </div>
      </Card>

      {/* Backup & Restore */}
      <Card title={<div className="flex items-center gap-2"><Database size={20} /> Backup & Restore</div>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
              <Download size={16} className="inline mr-1" /> Backup Data
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Download a complete JSON backup of all your data: products, customers, invoices, RMAs, repairs, GRNs, and settings.
            </p>
            <Button
              onClick={handleBackup}
              variant="primary"
              disabled={backupLoading || !isAdmin()}
              data-testid="backup-button"
            >
              <Download size={14} className="mr-2" />
              {backupLoading ? 'Creating backup...' : 'Download Backup'}
            </Button>
          </div>

          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-200 mb-2">
              <Upload size={16} className="inline mr-1" /> Restore Data
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 flex items-start gap-1">
              <AlertTriangle size={14} className="text-orange-600 flex-shrink-0 mt-0.5" />
              <span>Upload a backup JSON to restore data. You'll be asked to choose merge or wipe-and-replace.</span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              onChange={handleRestoreFile}
              className="hidden"
              data-testid="restore-file-input"
            />
            <Button
              onClick={handleRestoreClick}
              variant="danger"
              disabled={restoreLoading || !isSuperAdmin()}
              data-testid="restore-button"
            >
              <Upload size={14} className="mr-2" />
              {restoreLoading ? 'Restoring...' : 'Upload & Restore'}
            </Button>
            {!isSuperAdmin() && (
              <p className="text-xs text-orange-700 dark:text-orange-300 mt-2">Only superadmins can restore data</p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
